/**
 * Iter-111 — Backups page.
 *
 * Lists every metadata-backup CSV the project has generated (one per
 * UTC date, lazy-generated when first opened, plus any manually-
 * triggered backups). Owners + admins also get a "Generate backup
 * now" affordance for capturing an off-cycle snapshot before a
 * critical hand-off.
 *
 * The list is read-only beyond Generate Now + Download — backups are
 * never mutated or deleted in V1 (a date-keyed history acts as the
 * audit trail itself).
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Database, Check, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import ProjectHeader from "../components/ProjectHeader";
import ExternalBackupSection from "../components/ExternalBackupSection";


function formatBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}


function formatTimestamp(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_) {
        return iso;
    }
}


export default function BackupsPage() {
    const { projectId } = useParams();
    // Iter-158.13.2.20e.4 — respect the ApiBaseProvider so this page
    // works under /projects, /beta/hm-app/projects and /beta/projects.
    const apiBase = useApiBase();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Iter-158.13.2.20f.3 #6 — canonical Production backup-connection
    // status. One source of truth (`/backup/connection`) shared with
    // ProjectHeader.loadBackupStatus + HMBackupBanner. If backup is
    // "none"/"paused"/"error" the "Your production is protected"
    // hero copy is replaced with an honest state signal so this
    // surface no longer contradicts the header chip.
    const [connectionStatus, setConnectionStatus] = useState(null); // null|"none"|"paused"|"active"
    const [connectionCanProbe, setConnectionCanProbe] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data: p } = await api.get(`${apiBase}/${projectId}`);
            // Iter-158.13.2.20e.4 — Route 2 Hub GET returns
            // {project, script}; unwrap so we always work with a
            // flat DTO downstream (matches ProjectSettings pattern).
            const flat = (p && typeof p === "object" && p.project && p.project.id)
                ? p.project : p;
            setProject(flat);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
        // Iter-158.13.2.20f.3 #6 — canonical backup-connection probe.
        // Runs on the current apiBase so H&M mount reaches its own
        // gate (Members see 403 → connectionCanProbe stays false and
        // we render a calm neutral state instead of a lie).
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/backup/connection`);
            setConnectionCanProbe(true);
            if (!data?.connection) setConnectionStatus("none");
            else if (!data.connection.enabled) setConnectionStatus("paused");
            else setConnectionStatus("active");
        } catch {
            setConnectionCanProbe(false);
            setConnectionStatus(null);
        }
    }, [projectId, apiBase]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div data-testid="backups-page" className="min-h-screen bg-background grain">
            <ProjectHeader
                projectName={project?.name}
                subtitle="Storage"
            />

            {/* Iter-118.9 — body container standardised to the
                shared `max-w-2xl mx-auto px-5` primitive used by
                every other top-level screen. */}
            <main className="max-w-2xl mx-auto px-5 pt-6 pb-32 w-full">
                <header className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        {project?.name || ""}
                    </div>
                    <h1 className="font-display text-3xl font-black text-white mt-1 leading-none flex items-center gap-2">
                        <Database size={22} className="text-neutral-300" /> Storage
                    </h1>
                    <p
                        data-testid="backups-page-headline"
                        className="text-sm text-neutral-300 mt-3 leading-relaxed max-w-prose"
                    >
                        {/* Iter-158.13.2.20f.3 #6 — headline reflects the
                            canonical backup-connection status. No more
                            unconditional "protected". */}
                        {connectionStatus === "active"
                            ? "Your production is protected. Every upload is mirrored to your external storage."
                            : connectionStatus === "paused"
                                ? "External backup is paused. New uploads are stored in OnSetFlow but not mirrored to your external storage."
                                : connectionStatus === "none"
                                    ? "External backup is not connected yet. Uploads are stored safely in OnSetFlow, but nothing is mirrored to your own storage."
                                    : connectionCanProbe
                                        ? "Checking backup status…"
                                        : "Backup status is managed by Owner and Administrator. Ask the Production Owner to configure external backup."}
                    </p>
                </header>

                {loading ? (
                    <p
                        data-testid="backups-loading"
                        className="text-sm text-neutral-500 mb-4"
                    >
                        Loading…
                    </p>
                ) : null}
                {error ? (
                    <p
                        data-testid="backups-error"
                        className="text-sm text-red-300 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                {/* Iter-157O — "Where is my stuff?" answer card.
                    Aggressive simplification: the page only
                    answers ONE question (where things live). No
                    mention of restore points, exports, project
                    history or anything that belongs on a
                    different page. Six simple ticks — Scripts,
                    Photos, Documents, Notes, Continuity, Project
                    records — every item the producer might
                    wonder about, all confirmed stored. */}
                <section
                    data-testid="backups-where-card"
                    className={`rounded-xl border p-5 mb-6 ${
                        connectionStatus === "active"
                            ? "border-sky-500/25 bg-sky-500/[0.05]"
                            : "border-amber-500/30 bg-amber-500/[0.08]"
                    }`}
                >
                    <p
                        data-testid="backups-where-title"
                        className="text-sm text-white font-semibold mb-3 flex items-center gap-2"
                    >
                        {connectionStatus === "active" ? (
                            <>
                                <Check size={14} className="text-emerald-300" />
                                Your production is protected.
                            </>
                        ) : connectionStatus === "paused" ? (
                            <>
                                <AlertTriangle size={14} className="text-amber-300" />
                                Backup paused — new uploads are not being mirrored.
                            </>
                        ) : (
                            <>
                                <AlertTriangle size={14} className="text-amber-300" />
                                External backup not connected yet.
                            </>
                        )}
                    </p>
                    <ul
                        data-testid="backups-where-rows"
                        className="space-y-2 text-[13px] leading-relaxed text-neutral-200"
                    >
                        <li className="flex items-center gap-2" data-testid="backups-where-scripts">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Scripts</span>
                        </li>
                        <li className="flex items-center gap-2" data-testid="backups-where-photos">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Photos</span>
                        </li>
                        <li className="flex items-center gap-2" data-testid="backups-where-documents">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Documents</span>
                        </li>
                        <li className="flex items-center gap-2" data-testid="backups-where-notes">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Notes</span>
                        </li>
                        <li className="flex items-center gap-2" data-testid="backups-where-continuity">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Continuity</span>
                        </li>
                        <li className="flex items-center gap-2" data-testid="backups-where-project-records">
                            <Check size={14} className="text-emerald-300 shrink-0" />
                            <span>Project records</span>
                        </li>
                    </ul>
                </section>

                {/* Iter-115 — provider-driven external backup. Mirror
                    every uploaded file to Dropbox / future providers
                    asynchronously. The block self-hides for non-admins.
                    Iter-157N — section label retuned to plain language
                    so it reads as part of the "Where is my stuff?"
                    answer rather than as a numbered architecture
                    diagram. */}
                <div
                    data-testid="backups-section-provider"
                    className="mt-2 mb-2"
                >
                    <span className="text-[10px] uppercase tracking-[0.28em] text-neutral-500 font-semibold">
                        Connected storage
                    </span>
                </div>
                <ExternalBackupSection
                    projectId={projectId}
                    canManage={project?.role === "owner" || project?.role === "admin"}
                />

            </main>
        </div>
    );
}


